import { isThirdPartyLogistics } from "@redotech/account-common/team-type";
import { RedoMerchantRpcClient } from "@redotech/merchant-sdk-client/client";
import { GlobalSearchItem } from "@redotech/merchant-sdk/rpc/schema/global-search/global-search-schema";
import { useRequiredContext } from "@redotech/react-util/context";
import { useMerchantRoutes } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { GlobalSearchCategory } from "@redotech/redo-views-common/global-search/global-search-category";
import {
  CommandPaletteItem,
  SearchParams,
} from "@redotech/redo-web/command-palette/contexts/command-palette-context";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { usePageSearch } from "./global-search-tools/use-page-search";
import { globalSearchItemToCommandPaletteItem } from "./global-search/global-search-item-model";
import { useSettingsItems } from "./settings";
import {
  removeSettingsNavigationDuplicatePageResults,
  searchSettingsNavigationItems,
} from "./settings-navigation-search";

export function useSearchItems(merchantRpcClient: RedoMerchantRpcClient) {
  const routes = useMerchantRoutes();
  const team = useRequiredContext(TeamContext);
  const onlyAssignedFulfillments = isThirdPartyLogistics(team);
  const navigate = useNavigate();
  const searchPages = usePageSearch({ includeSettingsMetadata: true });
  const settingsItems = useSettingsItems();

  return useCallback(
    async ({
      query,
      filters,
      signal,
    }: SearchParams): Promise<CommandPaletteItem[]> => {
      if (!query.trim()) {
        return [];
      }

      // Determine category filter
      const category = onlyAssignedFulfillments
        ? "shipments"
        : filters.length === 1
          ? (filters[0] as GlobalSearchCategory)
          : undefined;

      // Search pages client-side
      const shouldSearchPages = !category || category === "pages";
      const settingsNavigationResults = shouldSearchPages
        ? searchSettingsNavigationItems({
            query,
            routes,
            sections: settingsItems ?? [],
            navigate,
            limit: category === "pages" ? 10 : 3,
            viewerVisibility: "merchant",
          })
        : [];
      const pageResults = shouldSearchPages
        ? searchPages(query, category === "pages" ? 10 : 3)
        : [];
      const dedupedPageResults = shouldSearchPages
        ? removeSettingsNavigationDuplicatePageResults({
            pageResults,
            settingsResults: settingsNavigationResults,
          })
        : [];

      // If only searching pages, return early
      if (category === "pages") {
        return [...settingsNavigationResults, ...dedupedPageResults].slice(
          0,
          10,
        );
      }

      // Fetch global search results from backend
      let globalSearchResults: CommandPaletteItem[] = [];
      try {
        const response = await merchantRpcClient.getGlobalSearchResults(
          { searchText: query, category },
          { signal },
        );

        globalSearchResults = response.results.map((item: GlobalSearchItem) =>
          globalSearchItemToCommandPaletteItem(item, routes, navigate),
        );
      } catch (error: unknown) {
        // Re-throw abort errors so caller can handle them appropriately
        if (error instanceof Error && error.name === "AbortError") {
          throw error;
        }
        console.warn("Failed to fetch global search results:", error);
      }

      // Combine results: pages + backend results
      return [
        ...settingsNavigationResults,
        ...dedupedPageResults,
        ...globalSearchResults,
      ];
    },
    [
      merchantRpcClient,
      onlyAssignedFulfillments,
      routes,
      navigate,
      searchPages,
      settingsItems,
    ],
  );
}
